import { Box, Divider, HStack, Image, Stack, VStack } from '@chakra-ui/react';

import { CVData, CVSettings, GDPR_CONSENT_TEXT } from '../types';
import { getDisplayPhotoUrl, hexToRgba } from '../utils/cvDefaults';
import { buildContactLinks, filterEmptyEntries, getDuplicateTitles, parseDescription } from '../utils/pdfUtils';

interface TemplateProps {
  data: CVData;
  settings: CVSettings;
}

const FONT_SIZE_MAP = {
  small: { name: '20px', title: '11px', body: '9px', section: '11px', contact: '8.5px', gdpr: '7px' },
  medium: { name: '24px', title: '12px', body: '10px', section: '12px', contact: '9px', gdpr: '7.5px' },
  large: { name: '28px', title: '14px', body: '11px', section: '14px', contact: '10px', gdpr: '8px' },
};

const ClassicTemplate = ({ data, settings }: TemplateProps) => {
  const { personalInfo, sections } = data;
  const sizes = FONT_SIZE_MAP[settings.fontSize];
  const accent = settings.accentColor;
  const accentBg = hexToRgba(accent, settings.accentOpacity ?? 1);
  const hasBg = settings.headerBackground;

  const displayPhotoUrl = getDisplayPhotoUrl(personalInfo.photoUrl, personalInfo.fullName);

  const contactItems = buildContactLinks(personalInfo);
  const hasPhoto = settings.showPhoto && !!displayPhotoUrl;

  return (
    <Box
      width="100%"
      backgroundColor="white"
      color="#1A202C"
      fontFamily={settings.fontFamily}
      minHeight="100%"
      overflowWrap="break-word"
      wordBreak="break-word"
    >
      {/* Header */}
      <Box
        px={8}
        pt={6}
        pb={5}
        backgroundColor={hasBg ? accentBg : 'gray.50'}
        color={hasBg ? 'white' : '#1A202C'}
      >
        <HStack spacing={5} alignItems="flex-start" justifyContent="space-between">
          {/* Left: Photo + Name + Title */}
          <HStack spacing={5} alignItems="center" flex={1}>
            {settings.showPhoto && displayPhotoUrl && (
              <Image
                src={displayPhotoUrl}
                alt={personalInfo.fullName}
                boxSize="72px"
                borderRadius="full"
                objectFit="cover"
                border="2px solid"
                borderColor={hasBg ? 'whiteAlpha.500' : accent}
                flexShrink={0}
              />
            )}
            <VStack spacing={1} alignItems="flex-start">
              <Box fontSize={sizes.name} fontWeight="bold" color={hasBg ? 'white' : '#1A202C'} lineHeight="1.2" letterSpacing="1px">
                {personalInfo.fullName || 'Your Name'}
              </Box>
              <Box fontSize={sizes.title} color={hasBg ? 'whiteAlpha.800' : '#4A5568'} fontWeight="medium" letterSpacing="0.5px">
                {personalInfo.jobTitle || 'Job Title'}
              </Box>
            </VStack>
          </HStack>

          {/* Right: Contact column */}
          {contactItems.length > 0 && (
            <VStack
              spacing={0.5}
              alignItems="flex-end"
              fontSize={sizes.contact}
              color={hasBg ? 'whiteAlpha.800' : '#4A5568'}
              flexShrink={0}
              mt={1}
            >
              {contactItems.map((item, i) => (
                <HStack key={i} spacing={1.5} alignItems="center">
                  <Box as="span" color={hasBg ? 'whiteAlpha.400' : accent} fontSize="5px" flexShrink={0}>●</Box>
                  {item.href ? (
                    <Box as="a" href={item.href} target="_blank" rel="noopener noreferrer" _hover={{ textDecoration: 'underline' }}>
                      {item.display}
                    </Box>
                  ) : (
                    <Box>{item.display}</Box>
                  )}
                  <Box as="span" color={hasBg ? 'whiteAlpha.400' : accent} fontSize="5px" flexShrink={0}>●</Box>
                </HStack>
              ))}
            </VStack>
          )}
        </HStack>
      </Box>
      {!hasBg && <Divider borderColor={accent} opacity={0.2} />}

      {/* Content area */}
      <Box px={8} pb={8} pt={personalInfo.summary || sections.length > 0 ? 4 : 0}>
        {/* Ordered content: About + Sections respecting aboutPosition */}
        {(() => {
          const aboutBlock = personalInfo.summary ? (
            <Box key="__about__" mb={4}>
              <Box
                fontSize={sizes.section}
                fontWeight="bold"
                color="#1A202C"
                letterSpacing="1.5px"
                mb={1}
              >
                {personalInfo.aboutTitle || 'About Me'}
              </Box>
              <Box fontSize={sizes.body} color="#2D3748" lineHeight="1.7">
                {personalInfo.summary}
              </Box>
            </Box>
          ) : null;

          const pos = data.aboutPosition ?? 0;
          const items: React.ReactNode[] = [];
          sections.forEach((section, i) => {
            if (i === pos && aboutBlock) items.push(aboutBlock);
            items.push(
          <Box key={section.id} mb={4}>
            <Box
              fontSize={sizes.section}
              fontWeight="bold"
              color="#1A202C"
              letterSpacing="1.5px"
              mb={0.5}
            >
              {section.title}
            </Box>
            <Divider borderColor={accent} opacity={0.2} mb={2} />

            {section.type === 'skills' ? (
              (() => {
                const filtered = filterEmptyEntries(section.entries);
                const dupes = getDuplicateTitles(filtered);
                const categorized = filtered.reduce<Record<string, string[]>>((acc, entry) => {
                  const category = entry.subtitle || 'General';
                  if (!acc[category]) acc[category] = [];
                  acc[category].push(entry.title);
                  return acc;
                }, {});

                const categories = Object.entries(categorized);

                return (
                  <Stack spacing={2}>
                    {categories.map(([category, skills]) => (
                      <HStack key={category} alignItems="flex-start" spacing={2}>
                        {categories.length > 1 && category !== 'General' && (
                          <Box
                            fontSize={sizes.body}
                            fontWeight="semibold"
                            color="#2D3748"
                            minWidth="100px"
                            flexShrink={0}
                          >
                            {category}
                          </Box>
                        )}
                        <HStack spacing={1} flexWrap="wrap">
                          {skills.map((skill, i) => {
                            const isDupe = dupes.has(skill.trim().toLowerCase());
                            return (
                              <Box
                                key={i}
                                fontSize={sizes.body}
                                px={2}
                                py={0.5}
                                backgroundColor={isDupe ? 'red.100' : 'gray.100'}
                                borderRadius="sm"
                                color={isDupe ? 'red.600' : '#2D3748'}
                                mb={0.5}
                              >
                                {skill}
                              </Box>
                            );
                          })}
                        </HStack>
                      </HStack>
                    ))}
                  </Stack>
                );
              })()
            ) : section.type === 'languages' ? (
              <HStack spacing={4} flexWrap="wrap">
                {filterEmptyEntries(section.entries).map((entry) => (
                  <HStack key={entry.id} spacing={1}>
                    <Box fontSize={sizes.body} fontWeight="semibold" color="#2D3748">
                      {entry.title}
                    </Box>
                    {entry.subtitle && (
                      <Box fontSize={sizes.body} color="#4A5568">
                        ({entry.subtitle})
                      </Box>
                    )}
                  </HStack>
                ))}
              </HStack>
            ) : section.type === 'hobbies' || section.type === 'interests' ? (
              (() => {
                const filtered = filterEmptyEntries(section.entries);
                const dupes = getDuplicateTitles(filtered);
                return (
                  <HStack spacing={1} flexWrap="wrap">
                    {filtered.map((entry) => {
                      const isDupe = dupes.has(entry.title.trim().toLowerCase());
                      return (
                        <Box
                          key={entry.id}
                          fontSize={sizes.body}
                          px={2}
                          py={0.5}
                          backgroundColor={isDupe ? 'red.100' : 'gray.100'}
                          borderRadius="sm"
                          color={isDupe ? 'red.600' : '#2D3748'}
                          mb={0.5}
                        >
                          {entry.title}
                        </Box>
                      );
                    })}
                  </HStack>
                );
              })()
            ) : section.type === 'certifications' ? (
              <Stack spacing={3}>
                {filterEmptyEntries(section.entries).map((entry) => (
                  <Box key={entry.id}>
                    <HStack justifyContent="space-between" alignItems="flex-start">
                      <VStack alignItems="flex-start" spacing={0} flex={1}>
                        <Box fontSize={sizes.title} fontWeight="bold" color="#1A202C">
                          {entry.title}
                        </Box>
                        {entry.subtitle && (
                          <Box fontSize={sizes.body} color="#4A5568">
                            {entry.subtitle}
                          </Box>
                        )}
                      </VStack>
                      {(entry.startDate || entry.endDate) && (
                        <Box fontSize={sizes.body} color="#4A5568" whiteSpace="nowrap" fontWeight="medium" textAlign="right">
                          {entry.startDate}
                          {entry.endDate && ` – ${entry.endDate}`}
                        </Box>
                      )}
                    </HStack>
                    {entry.location && (
                      <Box fontSize={sizes.body} color="#718096" mt={0.5}>
                        ID: {entry.location}
                      </Box>
                    )}
                    {entry.url && (
                      <Box as="a" href={entry.url} fontSize={sizes.body} color={accent} textDecoration="underline" mt={0.5} display="block">
                        Verify Credential
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Stack spacing={4}>
                {filterEmptyEntries(section.entries).map((entry) => (
                  <Box key={entry.id}>
                    <HStack justifyContent="space-between" alignItems="flex-start">
                      <VStack alignItems="flex-start" spacing={0} flex={1}>
                        <Box fontSize={sizes.title} fontWeight="bold" color="#1A202C">
                          {entry.title}
                        </Box>
                        {entry.subtitle && (
                          <Box fontSize={sizes.body} color="#4A5568">
                            {entry.subtitle}
                            {entry.location && (
                              <Box as="span" color="#718096">
                                {' '}— {entry.location}
                              </Box>
                            )}
                          </Box>
                        )}
                      </VStack>
                      {(entry.startDate || entry.endDate) && (
                        <Box
                          fontSize={sizes.body}
                          color="#4A5568"
                          whiteSpace="nowrap"
                          fontWeight="medium"
                          textAlign="right"
                        >
                          {entry.startDate}
                          {entry.endDate && ` – ${entry.endDate}`}
                        </Box>
                      )}
                    </HStack>

                    {entry.description && (() => {
                      const segments = parseDescription(entry.description);
                      return (
                        <Box mt={1}>
                          {segments.map((seg, si) => {
                            if (seg.type === 'break') return <Box key={si} height="6px" />;
                            if (seg.type === 'bullet') return (
                              <HStack key={si} spacing={1.5} alignItems="flex-start" mt={0.5} pl={0.5}>
                                <Box as="span" fontSize={sizes.body} color={accent} lineHeight="1.6">•</Box>
                                <Box as="span" fontSize={sizes.body} color="#2D3748" lineHeight="1.6">{seg.text}</Box>
                              </HStack>
                            );
                            return (
                              <Box key={si} fontSize={sizes.body} color="#2D3748" lineHeight="1.6">
                                {seg.text}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })()}

                    {entry.tags.length > 0 && (
                      <HStack spacing={1} mt={1} flexWrap="wrap">
                        {entry.tags.map((tag) => (
                          <Box
                            key={tag}
                            fontSize="8px"
                            px={1.5}
                            py={0.5}
                            backgroundColor="gray.100"
                            borderRadius="sm"
                            color="#2D3748"
                          >
                            {tag}
                          </Box>
                        ))}
                      </HStack>
                    )}

                    {(entry.url || entry.previewUrl) && (
                      <HStack spacing={3} mt={1} fontSize={sizes.body}>
                        {entry.url && (
                          <Box as="a" href={entry.url} color={accent} textDecoration="underline">
                            {entry.urlLabel || 'GitHub'}
                          </Box>
                        )}
                        {entry.previewUrl && (
                          <Box as="a" href={entry.previewUrl} color={accent} textDecoration="underline">
                            {entry.previewUrlLabel || 'Live Demo'}
                          </Box>
                        )}
                      </HStack>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
            );
          });
          if (pos >= sections.length && aboutBlock) items.push(aboutBlock);
          return items;
        })()}

        {/* GDPR Consent */}
        {personalInfo.gdprConsent && (
          <Box mt={6} pt={3} borderTop="1px solid" borderColor={`${accent}33`}>
            <Box fontSize={sizes.gdpr} color="#A0AEC0" lineHeight="1.5" fontStyle="italic">
              {GDPR_CONSENT_TEXT}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ClassicTemplate;
